import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";

const SCHEDULE_REFRESH_MS = 30 * 1000; // 30 detik
const HEARTBEAT_MS = 15 * 1000; // 15 detik
const CACHE_KEY = "signag…e_v1";

/* ─── Running Text Component ─── */
function RunningText({ text }) {
  // Hooks FIRST — never after early returns (React rules-of-hooks)
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const instanceId = useRef(`rt-${Math.random().toString(36).slice(2, 8)}`).current;
  const [offsetsReady, setOffsetsReady] = useState(false);
  const speed = (text && text.speed) || 50;
  const dir = (text && text.direction) || "left";
  const fontSize = (text && text.font_size) || 32;
  const animName = `scroll-${dir}-${instanceId}`;

  // Dynamic keyframes — always called (but no-ops when text/content missing)
  useEffect(() => {
    const c = containerRef.current;
    const t = textRef.current;
    if (!c || !t) return;

    const cw = c.offsetWidth;
    const ch = c.offsetHeight;
    const tw = t.scrollWidth;
    const th = t.scrollHeight;

    if (cw === 0 || tw === 0) return;

    let kf = "";
    if (dir === "left") {
      kf = `
        @keyframes ${animName} {
          0% { transform: translateX(${cw}px); }
          100% { transform: translateX(-${tw}px); }
        }
      `;
    } else if (dir === "right") {
      kf = `
        @keyframes ${animName} {
          0% { transform: translateX(-${tw}px); }
          100% { transform: translateX(${cw}px); }
        }
      `;
    } else if (dir === "up") {
      kf = `
        @keyframes ${animName} {
          0% { transform: translateY(${ch}px); }
          100% { transform: translateY(-${th}px); }
        }
      `;
    } else if (dir === "down") {
      kf = `
        @keyframes ${animName} {
          0% { transform: translateY(-${th}px); }
          100% { transform: translateY(${ch}px); }
        }
      `;
    } else {
      return; // unknown direction
    }

    let styleEl = document.getElementById(animName);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = animName;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = kf;
    setOffsetsReady(true);

    return () => {
      const el = document.getElementById(animName);
      if (el) el.remove();
    };
  }, [text && text.content, fontSize, speed, dir, animName]);

  // Early return for empty content
  if (!text || !text.content) {
    return (
      <div className="w-full h-full flex items-center justify-center"
        style={{ backgroundColor: "transparent" }}>
        <span className="text-zinc-500 text-xs">No text content</span>
      </div>
    );
  }

  const textLen = text.content.length;
  const duration = Math.max(5, Math.round(textLen / (speed / 10)));

  // Static centered text — transparent background
  if (dir === "none") {
    return (
      <div style={{
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: `${fontSize}px`,
        color: text.color || "#FFFFFF",
        backgroundColor: "transparent",
        fontFamily: "system-ui, sans-serif",
        fontWeight: 700,
        textAlign: "center",
        padding: "16px",
        boxSizing: "border-box",
      }}>{text.content}</div>
    );
  }

  // Scrolling text style
  const textStyle = {
    display: "inline-block",
    whiteSpace: "nowrap",
    fontSize: `${fontSize}px`,
    color: text.color || "#FFFFFF",
    fontFamily: "system-ui, sans-serif",
    fontWeight: 700,
    paddingRight: "10px",
  };

  if (offsetsReady) {
    textStyle.animation = `${animName} ${duration}s linear infinite`;
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full overflow-hidden flex items-center"
      style={{ backgroundColor: "transparent" }}
    >
      <div ref={textRef} style={textStyle}>{text.content}</div>
    </div>
  );
}


function ZonePlayer({ zone, playlist, onPlay, text }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);
  const videoDurRef = useRef(0);

  const advance = () => setIdx((i) => (i + 1) % playlist.length);
  const playlistKey = playlist?.map((p) => p.id).join("|") || "";
  const playlistLen = playlist?.length || 0;

  // Hooks MUST be called before any early return (React rules-of-hooks)
  useEffect(() => {
    setIdx(0);
    videoDurRef.current = 0;
  }, [playlistKey]);

  // Analytics only — does NOT arm timers (so refetch every ~30s can't reset them)
  useEffect(() => {
    if (!playlistLen) return;
    const item = playlist[idx];
    onPlay && onPlay(item);
  }, [idx, playlist, onPlay]);

  // Safety timer — armed ONLY when idx or playlist CONTENT changes (primitive deps),
  // so periodic schedule refetches never reset it.
  useEffect(() => {
    if (!playlistLen) return;
    const item = playlist[idx];
    clearTimeout(timerRef.current);
    if (item.type === "image") {
      timerRef.current = setTimeout(advance, (item.duration_sec || 8) * 1000);
    } else if (playlistLen > 1) {
      // SAFETY FALLBACK for videos: if onEnded never fires (stalled/buffering),
      // force-advance after video duration (+5s margin) so playback never freezes.
      // Guard against browsers reporting Infinity/NaN/0 duration.
      let dur = videoDurRef.current;
      if (!isFinite(dur) || dur <= 0) dur = 60;
      const ms = Math.min(dur, 600) * 1000 + 5000; // cap 10 min, +5s margin
      timerRef.current = setTimeout(advance, ms);
    }
    return () => clearTimeout(timerRef.current);
  }, [idx, playlistKey, playlistLen]);

  // TEXT ZONE — render running text (hooks called above, safe to return early now)
  if (zone.type === "text") {
    return (
      <div
        className="absolute overflow-hidden"
        style={{ left: `${zone.x}%`, top: `${zone.y}%`, width: `${zone.w}%`, height: `${zone.h}%` }}
        data-testid={`zone-${zone.id}`}
      >
        <RunningText text={text} />
      </div>
    );
  }

  if (!playlist?.length) {
    return (
      <div className="absolute flex items-center justify-center text-zinc-500 text-xs uppercase tracking-[0.3em]"
        style={{ left: `${zone.x}%`, top: `${zone.y}%`, width: `${zone.w}%`, height: `${zone.h}%` }}>
        {zone.name}
      </div>
    );
  }

  const item = playlist[idx];
  const isImage = item.type === "image";
  return (
    <div
      className="absolute overflow-hidden bg-black"
      style={{
        left: `${zone.x}%`, top: `${zone.y}%`,
        width: `${zone.w}%`, height: `${zone.h}%`,
      }}
      data-testid={`zone-${zone.id}`}
    >
      {isImage ? (
        <img
          key={item.id + "-" + idx}
          src={fileUrl(item.url)}
          alt=""
          className="signage-fade"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <video
          key={item.id + "-" + idx}
          ref={videoRef}
          src={fileUrl(item.url)}
          autoPlay
          muted
          playsInline
          loop={playlist.length <= 1}
          onLoadedMetadata={(e) => {
            const d = e.target.duration;
            videoDurRef.current = isFinite(d) ? d : 0;
            if (playlist.length > 1 && item.type !== "image") {
              // Re-arm safety timer with real duration once metadata is known
              clearTimeout(timerRef.current);
              let dur = videoDurRef.current;
              if (dur <= 0) dur = 60;
              const ms = Math.min(dur, 600) * 1000 + 5000;
              timerRef.current = setTimeout(advance, ms);
            }
          }}
          onEnded={() => {
            if (playlist.length <= 1) return;
            clearTimeout(timerRef.current);
            advance();
          }}
          className="signage-fade"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
}

export default function Player() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [offline, setOffline] = useState(!navigator.onLine);
  const lastRefreshAtRef = useRef(null);
  const deviceId = typeof window !== "undefined" ? localStorage.getItem("signage_device_id") : null;

  useEffect(() => {
    if (!deviceId) nav("/pair", { replace: true });
  }, [deviceId, nav]);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    const onErr = () => setTimeout(() => window.location.reload(), 5000);
    window.addEventListener("error", onErr);
    return () => window.removeEventListener("error", onErr);
  }, []);

  const loadSchedule = async () => {
    if (!deviceId) return;
    try {
      const { data: s } = await api.get(`/player/schedule?device_id=${deviceId}`);
      setData(s);
      setErr("");
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(s)); } catch {}
    } catch (e) {
      setErr(formatApiError(e));
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) setData(JSON.parse(cached));
      } catch {}
    }
  };

  useEffect(() => {
    loadSchedule();
    const id = setInterval(loadSchedule, SCHEDULE_REFRESH_MS);
    return () => clearInterval(id);
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) return;
    const tick = async () => {
      try {
        const { data: hb } = await api.post(`/player/heartbeat?device_id=${deviceId}`);
        if (hb.force_refresh_at && hb.force_refresh_at !== lastRefreshAtRef.current) {
          if (lastRefreshAtRef.current !== null) {
            // 1) Refresh data immediately (no flash)
            await loadSchedule();
            // 2) Reload page after a moment so new JS/CSS is picked up
            setTimeout(() => window.location.reload(), 2000);
            return;
          }
          lastRefreshAtRef.current = hb.force_refresh_at;
        } else if (lastRefreshAtRef.current == null && hb.force_refresh_at) {
          lastRefreshAtRef.current = hb.force_refresh_at;
        }
      } catch {}
    };
    tick();
    const id = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [deviceId]);

  const onPlay = (item) => {
    if (!deviceId || !item) return;
    api.post(`/analytics/log?device_id=${deviceId}&media_id=${item.id}`).catch(() => {});
  };

  if (!deviceId) return null;

  const layout = data?.layout;
  const zones = data?.zones || [];
  const hasAny = zones.some((z) => z.playlist?.length);

  return (
    <div className="player-root" data-testid="player-root">
      {layout ? (
        <div className="absolute inset-0">
          {zones.map((z) => (
            <ZonePlayer key={z.zone.id} zone={z.zone} playlist={z.playlist} text={z.text} onPlay={onPlay} />
          ))}
          {!hasAny && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white/80">
              <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">{data?.device?.name}</p>
              <h1 className="font-display text-4xl sm:text-6xl font-black mt-3">No content scheduled</h1>
              <p className="text-sm text-zinc-500 mt-2">Waiting for the next scheduled slot…</p>
            </div>
          )}
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-500">{data?.device?.name || "Display"}</p>
          <h1 className="font-display text-4xl sm:text-6xl font-black mt-3">Assign a layout</h1>
          <p className="text-sm text-zinc-500 mt-2">Open admin → Devices to attach a layout and schedule.</p>
          {err && <p className="mt-6 text-xs text-red-400">{err}</p>}
        </div>
      )}

      {offline && (
        <div className="fixed bottom-3 left-3 bg-amber-500 text-black text-[10px] uppercase tracking-widest px-3 py-1 font-bold" data-testid="offline-indicator">
          Offline — showing cached
        </div>
      )}
    </div>
  );
}
